import classNames from "classnames"
import { ComponentProps, ReactNode } from "react"
import { Button } from "../Button"
import { Heading, Headings } from "../Heading"
import { Icon } from "../Icon"
import { Text } from "../Text"
import { EmptyStateProps } from "./types"
import { EmptyStateIllustration } from "./illustrations"

type EmptyStateHeadingProps = {
  /**
   * Tag of the heading element
   * @default "h2"
   */
  as?: Headings
  children: string
}

function EmptyStateHeading({ children, as = "h2" }: EmptyStateHeadingProps) {
  return (
    <Heading as={as} size="xl" className="mt-3">
      {children}
    </Heading>
  )
}

function EmptyStateContent({ children }: { children: ReactNode }) {
  if (typeof children === "string") {
    return (
      <Text className="mt-1" size="medium">
        {children}
      </Text>
    )
  }
  return <div className="mt-1">{children}</div>
}

function EmptyStateActions({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto mt-3 flex flex-col gap-x-4 gap-y-4 md:w-max md:flex-row-reverse">
      {children}
    </div>
  )
}

function EmptyStateActionButton({ children, ...rest }: ComponentProps<typeof Button>) {
  return (
    <Button {...rest} isFullWidth className="mx-auto w-full md:w-fit">
      {children}
    </Button>
  )
}

/**
 * Component for when there is no data to display
 * This is basic example of how to use the [EmptyState component](http://storybook.oysterhr.com/?path=/docs/design-system-emptystate-docs--docs)
 * ```
 * <EmptyState>
 *   <EmptyState.Heading>You have no outstanding invoices</EmptyState.Heading>
 *   <EmptyState.Content>
 *     You will see your next invoices appear here. But for now, you are all settled up
 *   </EmptyState.Content>
 *   <EmptyState.Actions>
 *     <EmptyState.ActionButton>Back to dashboard</EmptyState.ActionButton>
 *   </EmptyState.Actions>
 * </EmptyState>
 * ```
 */
export function EmptyState({
  children,
  className,
  iconName,
  illustrationSrc = EmptyState.Illustration.rocket,
  illustrationAlt = "",
}: EmptyStateProps) {
  return (
    <div className={classNames("text-center text-black", className)}>
      <div className="mx-auto grid grid-cols-1 grid-rows-1 place-items-center">
        <div
          className={classNames(
            "col-start-1 row-start-1 h-14 w-14 rounded-full bg-primary-lighten-100",
            {
              "border border-primary-lighten-50": iconName,
            },
          )}
          role="presentation"
        />
        {iconName && (
          <Icon
            className="col-start-1 row-start-1 text-primary-base"
            size="md"
            name={iconName}
            role="presentation"
          />
        )}
        {!iconName && (
          <img className="col-start-1 row-start-1" alt={illustrationAlt} src={illustrationSrc} />
        )}
      </div>
      {children}
    </div>
  )
}

EmptyState.Content = EmptyStateContent
EmptyState.Heading = EmptyStateHeading
EmptyState.Actions = EmptyStateActions
EmptyState.ActionButton = EmptyStateActionButton
EmptyState.Illustration = EmptyStateIllustration
